import { useState, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { MbscCalendarColor } from '@mobiscroll/react';
import { colors } from '@bridebook/ui';
import { ShowroundCalendarSlot } from '../types/types';
import { useSelector } from 'react-redux';
import { getShowroundDefaultDate } from '../state/selectors';
import { DEFAULT_DATA_TIMEZONE } from '../constants';

interface CalendarSelectionState {
  selectedDate: string | null;
  selectedSlot: string | null;
}

interface CalendarSelectionActions {
  selectDate: (date: string) => void;
  selectSlot: (slotId: string) => void;
  clearSelection: () => void;
}

interface CalendarSelectionComputed {
  availableDates: MbscCalendarColor[];
  validDates: string[];
  selectedDateSlots: ShowroundCalendarSlot[];
  hasSelection: boolean;
  isDateSelected: (date: string) => boolean;
  isSlotSelected: (slotId: string) => boolean;
  canProceedToBooking: boolean;
}

export interface UseCalendarSelectionReturn
  extends CalendarSelectionState,
    CalendarSelectionActions,
    CalendarSelectionComputed {}

/**
 * Custom hook that encapsulates calendar selection behavior.
 * This hook manages the complex state relationships between dates and slots,
 * provides computed values, and handles side effects.
 */
export const useCalendarSelection = (
  slots: ShowroundCalendarSlot[] = [],
  timezone?: string,
): UseCalendarSelectionReturn => {
  const defaultSelectionDate = useSelector(getShowroundDefaultDate);
  const [selectedDate, setSelectedDate] = useState<string | null>(defaultSelectionDate ?? null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Selects a date from the showrounds calendar datepicker
  const selectDate = useCallback((date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  }, []);

  // Selects a slot from the showrounds calendar timepicker
  const selectSlot = useCallback((slotId: string) => {
    setSelectedSlot(slotId);
  }, []);

  // Clears the selected date and slot (e.g. when user closes the modal of after sucessfully booking)
  const clearSelection = useCallback(() => {
    setSelectedDate(null);
    setSelectedSlot(null);
  }, []);

  const { availableDates: baseDates, validDates } = useMemo(() => {
    if (!slots.length) {
      return { availableDates: [], validDates: [] };
    }

    const uniqueDates = new Set<string>();
    slots.forEach((slot) => {
      const slotDate = DateTime.fromISO(slot.start, {
        zone: timezone || DEFAULT_DATA_TIMEZONE,
      }).toISODate();
      if (slotDate) {
        uniqueDates.add(slotDate);
      }
    });

    const dateArray = Array.from(uniqueDates).sort();
    return { availableDates: dateArray, validDates: dateArray };
  }, [slots, timezone]);

  const availableDates = useMemo(
    () =>
      baseDates.map((dateStr) => {
        const isSelected = dateStr === selectedDate;
        return {
          date: dateStr,
          ...(isSelected ? {} : { highlight: colors.indigoCrush10 }),
        };
      }),
    [baseDates, selectedDate],
  );

  const slotsByDate = useMemo(() => {
    const map = new Map<string, ShowroundCalendarSlot[]>();
    slots.forEach((slot) => {
      const slotDate = DateTime.fromISO(slot.start, {
        zone: timezone || DEFAULT_DATA_TIMEZONE,
      }).toISODate();
      if (slotDate) {
        if (!map.has(slotDate)) {
          map.set(slotDate, []);
        }
        const dateSlots = map.get(slotDate);
        if (dateSlots) {
          dateSlots.push(slot);
        }
      }
    });

    map.forEach((dateSlots) =>
      dateSlots.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    );

    return map;
  }, [slots, timezone]);

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDate.get(selectedDate) || [];
  }, [selectedDate, slotsByDate]);

  const isDateSelected = useCallback((date: string) => date === selectedDate, [selectedDate]);
  const isSlotSelected = useCallback((slotId: string) => slotId === selectedSlot, [selectedSlot]);

  const hasSelection = Boolean(selectedDate && selectedSlot);
  const canProceedToBooking = useMemo(() => {
    if (!hasSelection) {
      return false;
    }
    return selectedDateSlots.find((slot) => slot.start === selectedSlot) !== undefined;
  }, [hasSelection, selectedDateSlots, selectedSlot]);

  return {
    // State
    selectedDate,
    selectedSlot,

    // Actions
    selectDate,
    selectSlot,
    clearSelection,

    // Dates with available slots - used to highlight the dates in the datepicker
    availableDates,
    // Dates with available slots - used to disable the dates in the datepicker
    validDates,
    // Used to mark Today's date in the datepicker
    selectedDateSlots,
    // Whether a date and slot are selected
    hasSelection,
    // Whether a date is selected
    isDateSelected,
    // Whether a slot is selected
    isSlotSelected,
    // Whether the user can proceed to booking
    canProceedToBooking,
  };
};
